import React, { useEffect, useCallback, useState } from 'react';
import { Select, Row, Table, Tooltip, Button, Modal } from 'antd';
import Navbar from 'src/common/Navbar/Navbar';
import { LabelValue } from 'src/library/types';
import {
  AssignedDriversProps,
  AssignedDriversRecord,
  identificationDocumentsColumns,
} from 'src/components/pages/AssignedDrivers/AssignedDrivers.utils';
import { ReloadOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import {
  getAssignedDrivers,
} from 'src/components/pages/AssignedDrivers/AssignedDrivers.actionsCreator';
import { formatDateTime } from 'src/utils/utils';
import { AssignedDriverStyles } from 'src/components/pages/AssignedDrivers/AssignedDrivers.styles';
import SimplePaginator from 'src/common/SimplePaginator';
import VendorCodeFilter from 'src/components/vendorcodefilter/VendorCodeFilter';
import {
  SET_ASSIGNED_DRIVERS_PAGINATION,
} from 'src/common/actions/constants';
import AddNewDriverModal from './AddNewDriverModal';
import TableModal from 'src/common/TableModal';

const AssignedDrivers: React.FC<AssignedDriversProps> = (props: AssignedDriversProps) => {
  const {
    classes,
    assignedDrivers,
    getAssignedDrivers,
    setPagination,
    columnList,
    vendorConfig,
    t,
  } = props;

  const { pagination } = assignedDrivers;
  const allow_driver_creation = vendorConfig?.worker_master_config?.allow_driver_creation || false;
  const allow_driver_update = vendorConfig?.worker_master_config?.allow_driver_update || false;

  const [filters, setFilters] = useState({
    vendorIdsToFilter: [] as string[],
  });

  const [state, setState] = useState({
    isDriverModalVisible: false,
    isIdentificationDocModalVisible: false,
    action: undefined,
    selectedWorker: null,
    selectedVendors: [] as LabelValue[],
  });

  const fetchViewDetailsAndCount = () => {
    getAssignedDrivers({ ...pagination, ...filters });
  };

  useEffect(() => {
    fetchViewDetailsAndCount();
  }, [pagination, filters]);

  const handleCloseModal = (modalName: string) => {
    setState((prevState) => ({
      ...prevState,
      [modalName]: false,
      action: undefined,
      selectedWorker: null,
    }));
  };

  const showModals = useCallback((modalName: string, record?: any, action?: string) => {
    setState((prevState) => ({
      ...prevState,
      [modalName]: true,
      action: action as 'create' | 'edit' | undefined,
      selectedWorker: record || null,
    }));
  }, []);

  const handleVendorFilterChange = (value: LabelValue | LabelValue[]) => {
    const vendors = Array.isArray(value) ? value : [];
    setState((prevState) => ({
      ...prevState,
      selectedVendors: vendors,
    }));
    const vendorIds = vendors.map((item) => item.value as string);
    setFilters({ vendorIdsToFilter: vendorIds });
  };

  const generateColumnsToShow = useCallback(() => {
    const columnData: {
      title: string;
      sorter: boolean;
      dataIndex: string;
      key: string;
      width: number;
      render?: (value: any, record: AssignedDriversRecord) => JSX.Element | string;
    }[] = [];
    columnList?.forEach((item) => {
      const column_id = item.is_sortable ? item.sort_key : item.column_id;
      const itemToPush = {
        title: t(item.pretty_name),
        sorter: item.is_sortable,
        dataIndex: column_id,
        key: column_id,
        width: 190,
      };

      if (column_id === 'identification_documents' && !allow_driver_creation) {
          return;
      }
      if (column_id === 'actions' && !allow_driver_update) {
        return;
      }

      if (column_id === 'created_at') {
        itemToPush['render'] = (x) => {
          if (x) return formatDateTime(x);
          return 'N/A';
        };
      }
      if (column_id === 'driving_license_number') {
        itemToPush['render'] = (_, record) => _;
      }
      if (column_id === 'name') {
        itemToPush['render'] = (_, record) => _;
      }
      if (column_id === 'worker_code') {
        itemToPush['render'] = (_, record) => _;
      }
      if (column_id === 'is_active') {
        itemToPush['render'] = (_, record) => (
          record.is_active ? <CheckOutlined /> : <CloseOutlined />
        );
      }
      if (column_id === 'actions') {
        itemToPush['render'] = (_, record) => {
          return allow_driver_update ? (
            <Button
              onClick={() => showModals('isDriverModalVisible', record, 'edit')}
              disabled={!record.is_active}
            >
              {t('Edit')}
            </Button>
          ) : null;
        };
      }
      if (column_id === 'identification_documents') {
        itemToPush['render'] = (_, record) => {
          const documents = record?.identification_documents;
          return Array.isArray(documents) && documents.length ? (
            <Button
              type="link"
              onClick={() => showModals('isIdentificationDocModalVisible', { identification_documents: documents })}
            >
              {t('View')}
            </Button>
          ) : (
            '---'
          );
        };
      }
      columnData.push(itemToPush);
    });
    return columnData;
  }, []);

  const renderFilters = () => {
    return (
      <Row style={{ background: '#fff', padding: '12px 16px', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <VendorCodeFilter
            value={state.selectedVendors}
            onChange={handleVendorFilterChange}
            placeholder={t('Vendor Code')}
            mode="multiple"
            style={{ minWidth: 200 }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexGrow: 1 }}>
          {allow_driver_creation && <Button onClick={() => showModals('isDriverModalVisible', null, 'create')}>{t('Add New Driver')}</Button>}
          <SimplePaginator
            loading={assignedDrivers.loader}
            onFirstClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber,
                nextOrPrev: 'first',
              });
            }}
            onPrevClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber - 1,
                nextOrPrev: 'prev',
              });
            }}
            onNextClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber + 1,
                nextOrPrev: 'next',
              });
            }}
            isNext={assignedDrivers.is_next_present}
            pageNumber={pagination.pageNumber}
          />
          <Select
            className={classes.selectWithoutBorder}
            onChange={(resultPerPage) => {
              setPagination({ ...pagination, resultPerPage });
            }}
            value={pagination.resultPerPage}
            style={{ marginLeft: 16 }}
          >
            <Select.Option value={100}>{t('100/page')}</Select.Option>
            <Select.Option value={50}>{t('50/page')}</Select.Option>
            <Select.Option value={20}>{t('20/page')}</Select.Option>
            <Select.Option value={10}>{t('10/page')}</Select.Option>
          </Select>
          <Tooltip title={t("Refresh")}>
            <Button
              onClick={fetchViewDetailsAndCount}
              className={classes.refreshButton}
              style={{ marginLeft: 16 }}
            >
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>
      </Row>
    );
  };

  const renderViewTable = () => (
    <Table
      rowKey={(record: AssignedDriversRecord) => record['id']}
      className={classes.table}
      dataSource={assignedDrivers.page_data || []}
      columns={generateColumnsToShow()}
      loading={assignedDrivers.loader}
      pagination={false}
    />
  );

  const renderNavbar = () => (
    <Navbar
      title={t("Assigned Drivers")}
    />
  );

  return (
    <div className={classes.mainDiv}>
      {state.isDriverModalVisible && (
        <AddNewDriverModal
          isVisible={state.isDriverModalVisible}
          handleModalClose={() => handleCloseModal('isDriverModalVisible')}
          action={state.action}
          driverData={state.selectedWorker}
          reloadView={fetchViewDetailsAndCount}
        />
      )}
      { state.isIdentificationDocModalVisible && (
        <TableModal
          header={t("Identification Documents")}
          isVisible={state.isIdentificationDocModalVisible}
          handleModalClose={() => handleCloseModal('isIdentificationDocModalVisible')}
          dataSource={state.selectedWorker['identification_documents']}
          columnsToShow={identificationDocumentsColumns}
        />
      )}
      {renderNavbar()}
      <div className={classes.tabsContainer}>
        {renderFilters()}
        {renderViewTable()}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    assigned_worker_column_list,
    vendor_config,
  } = state.masterData;
  return {
    columnList: assigned_worker_column_list,
    vendorConfig: vendor_config,
    assignedDrivers: state.assignedDrivers,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setPagination: (data) =>
      dispatch({
        type: SET_ASSIGNED_DRIVERS_PAGINATION,
        data,
      }),
    getAssignedDrivers: (data) => dispatch(getAssignedDrivers(data)),
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: AssignedDriverStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(AssignedDrivers);
